import type { ReactNode } from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cx } from "class-variance-authority";

import { Button } from "../Button/Button";
import { Close as CloseIcon } from "../Icon/Close";
import { Danger as DangerIcon } from "../Icon/Danger";
import { CheckCircle as CheckIcon } from "../Icon/CheckCircle";
import { Info as InfoIcon } from "../Icon/Info";
import { Warning as WarningIcon } from "../Icon/Warning";
import * as styles from "./Alert.module.scss";

export type AlertProps = {
  className?: string;
  icon?: ReactNode;
  children?: ReactNode;
  actions?: ReactNode;
  isActionsInline?: boolean;
  title?: string;
  contentClassName?: string;
  onDismiss?: () => void;
} & VariantProps<typeof alertVariants>;

const alertVariants = cva(styles.alert, {
  variants: {
    color: {
      danger: styles.danger,
      warning: styles.warning,
      info: styles.info,
      success: styles.success,
      neutral: styles.neutral,
    },
    indicator: {
      top: styles.topIndicator,
      bottom: styles.bottomIndicator,
    },
    size: {
      small: styles.small,
      default: styles.sizeDefault,
      large: styles.large,
    },
  },
  defaultVariants: {
    color: "info",
    size: "large",
  },
});

const defaultIconsForColor = {
  danger: <DangerIcon />,
  warning: <WarningIcon />,
  info: <InfoIcon />,
  success: <CheckIcon />,
  neutral: <InfoIcon />,
};

const Alert = ({
  icon,
  children,
  color = "info",
  className,
  contentClassName,
  actions,
  isActionsInline,
  title,
  indicator,
  size,
  onDismiss,
}: AlertProps) => {
  const defaultIcon = defaultIconsForColor[color || "info"];
  const actualIcon = icon !== undefined ? icon : defaultIcon;

  return (
    <div
      className={cx(alertVariants({ color, indicator, size }), className)}
      data-color={color || "info"}
      role="alert"
    >
      <div
        className={cx(styles.iconWithContent, {
          [styles.topAlign]: !!title,
        })}
      >
        {actualIcon && (
          <div data-testid="icon" className={styles.icon}>
            {actualIcon}
          </div>
        )}
        <div
          className={cx(styles.content, contentClassName, {
            [styles.rightPadding]: onDismiss,
          })}
        >
          {title && <div className={styles.title}>{title}</div>}
          {children}
        </div>
        {actions && isActionsInline && (
          <div className={styles.inlineActions}>{actions}</div>
        )}
        {onDismiss && (
          <Button className={styles.close} onClick={onDismiss} variant="link">
            <CloseIcon />
          </Button>
        )}
      </div>
      {actions && !isActionsInline && (
        <div className={styles.actions}>{actions}</div>
      )}
    </div>
  );
};

export { Alert, alertVariants };
